import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { TenantStatus } from "@chat-yai/shared";
import { superApi } from "@/api/endpoints/super";
import { Icon } from "@/components/Icon";

type StatusFilter = "all" | TenantStatus;

export function SuperTenantsPage(): JSX.Element {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("created_desc");

  const query = useQuery({
    queryKey: ["super", "tenants", { statusFilter, q, page, sort }],
    queryFn: () =>
      superApi.listTenants({
        status: statusFilter === "all" ? undefined : statusFilter,
        q: q || undefined,
        page,
        page_size: 20,
        sort,
      }),
  });
  const totalPages = Math.max(1, Math.ceil((query.data?.total ?? 0) / 20));

  return (
    <div className="page" data-testid="super-tenants-page">
      <div className="page-head">
        <div>
          <div className="page-title">Tenants</div>
          <div className="page-sub">All workspaces on the platform.</div>
        </div>
      </div>

      <div className="filters-bar">
        <div className="search-input">
          <Icon name="search" size={14} />
          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            placeholder="Search slug or name…"
            data-testid="super-search"
          />
        </div>
        <div className="seg" data-testid="super-status-seg">
          {(["all", "active", "suspended"] as StatusFilter[]).map((s) => (
            <button
              key={s}
              className={statusFilter === s ? "active" : ""}
              onClick={() => {
                setStatusFilter(s);
                setPage(1);
              }}
              data-testid={`super-status-${s}`}
            >
              {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
        <select
          className="field-input"
          style={{ width: 200, height: 34 }}
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          aria-label="Sort"
          data-testid="super-sort"
        >
          <option value="created_desc">Newest first</option>
          <option value="created_asc">Oldest first</option>
          <option value="name_asc">Name A-Z</option>
          <option value="name_desc">Name Z-A</option>
        </select>
      </div>

      <div className="card">
        <div
          className="table-row-head"
          style={{ gridTemplateColumns: "32px 2fr 1.5fr 1fr 100px 100px" }}
        >
          <div></div>
          <div>Tenant</div>
          <div>Plan</div>
          <div>Status</div>
          <div>Members</div>
          <div>Created</div>
        </div>
        {query.data?.items.map((t) => (
          <Link
            to="/super/tenants/$id"
            params={{ id: t.id }}
            key={t.id}
            className="list-row"
            style={{
              gridTemplateColumns: "32px 2fr 1.5fr 1fr 100px 100px",
              cursor: "pointer",
              color: "inherit",
            }}
            data-testid={`super-tenant-row-${t.slug}`}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                background: "var(--accent-soft)",
                color: "var(--accent)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 600,
                fontSize: 12,
              }}
            >
              {t.name[0]?.toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{t.name}</div>
              <div className="mono" style={{ fontSize: 11, color: "var(--text-3)" }}>
                {t.slug}
              </div>
            </div>
            <div>
              <span className="chip">{t.plan_name}</span>
            </div>
            <div>
              <span
                className={
                  "chip " + (t.status === "active" ? "chip-success" : "chip-warning")
                }
              >
                <span className="chip-dot" />
                {t.status}
              </span>
            </div>
            <div className="mono" style={{ fontSize: 12 }}>
              {t.member_count}
            </div>
            <div className="mono" style={{ fontSize: 11, color: "var(--text-3)" }}>
              {new Date(t.created_at).toLocaleDateString()}
            </div>
          </Link>
        ))}
        {(query.data?.items.length ?? 0) === 0 ? (
          <div className="empty">No tenants match.</div>
        ) : null}
        <div className="pagination">
          <span>
            Page {page} of {totalPages} · {query.data?.total ?? 0} tenants
          </span>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              className="btn btn-outline btn-sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <button
              className="btn btn-outline btn-sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
